import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import PageHeader from '../components/common/PageHeader';
import ImageUploader from '../components/common/ImageUploader';
import {
  getCertifications,
  createCertification,
  deleteCertification,
} from '../services/certificationsApi';

const Certifications = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newCertUrl, setNewCertUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const data = await getCertifications();
      setItems(data.items);
    } catch (err) {
      console.error('Failed to fetch certifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await createCertification(newName.trim(), newImageUrl.trim(), newCertUrl.trim());
      setDialogOpen(false);
      setNewName('');
      setNewImageUrl('');
      setNewCertUrl('');
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certification? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await deleteCertification(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Certifications / Awards" subtitle="Manage certifications — select them on each product page" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Certifications / Awards"
        subtitle="Manage certifications — select them on each product page"
        action={
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => setDialogOpen(true)}>
            Add Certification
          </Button>
        }
      />

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <WorkspacePremiumRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No certifications added
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Certification" to create your first entry
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 160 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 200 }}>
                    Image URL
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 200 }}>
                    URL
                  </TableCell>
                  <TableCell align="center" sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.image_url ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.image_url}
                          </Typography>
                          <Tooltip title="Download / Open link">
                            <IconButton
                              size="small"
                              component={Link}
                              href={item.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'primary.main' }}
                            >
                              <DownloadRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">--</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.cert_url ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.cert_url}
                          </Typography>
                          <Tooltip title="Open link">
                            <IconButton
                              size="small"
                              component={Link}
                              href={item.cert_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'primary.main' }}
                            >
                              <DownloadRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">--</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          disabled={deleting === item.id}
                          sx={{ color: 'error.main' }}
                        >
                          {deleting === item.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteOutlineRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setError(''); setNewName(''); setNewImageUrl(''); setNewCertUrl(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add Certification / Award</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            placeholder="e.g. BIFMA Certified"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setError(''); }}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            error={!!error}
            helperText={error}
          />
          <Box sx={{ mb: 2 }}>
            <ImageUploader value={newImageUrl} onChange={setNewImageUrl} folder="certifications" label="Certification Image" />
          </Box>
          <TextField
            label="URL"
            placeholder="https://example.com/certification"
            value={newCertUrl}
            onChange={(e) => setNewCertUrl(e.target.value)}
            fullWidth
            helperText="Link to the certification page"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); setError(''); setNewName(''); setNewImageUrl(''); setNewCertUrl(''); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Certifications;
